import React, { useCallback, useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { DownOutlined, MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import { FctIsotipo, FctLogotipo } from '../../fct/react';
import UserProfile from '../Auth/UserProfile';
import { useMenuItems, type MenuItemConfig } from './menuItems';
import '../../estilos/gescomaux.css';

/**
 * GcCarcasa — carcasa interna de GescomAux según el manual de marca digital v0.9.1 (piel A):
 * raíl verde oscuro a la izquierda con el isotipo de entorno `2x4-06` en una tinta (inversa
 * sobre el raíl) y el logotipo «gescom.aux» en Dissolve, lienzo tenue para el contenido.
 *
 * Es una adaptación de FctCarcasa (src/fct/react) porque GescomAux es un entorno, no una
 * herramienta: el isotipo va en una tinta, el raíl lleva icono y etiqueta (R-05) y navega
 * con NavLink sin recargar la SPA. Reutiliza las clases fct-carcasa / fct-rail / fct-principal.
 *
 * El raíl se puede plegar a 60 px (solo iconos, con tooltip); la preferencia se guarda en
 * localStorage. En pantallas de 480 px o menos pasa a barra inferior (fct.css).
 */
const CLAVE_PLEGADO = 'gcaux.rail.plegado';

function leerPlegado(): boolean {
  try {
    return window.localStorage.getItem(CLAVE_PLEGADO) === '1';
  } catch {
    return false;
  }
}

function guardarPlegado(valor: boolean) {
  try {
    window.localStorage.setItem(CLAVE_PLEGADO, valor ? '1' : '0');
  } catch {
    /* sin almacenamiento: la preferencia dura la sesión */
  }
}

/** Enlace del raíl (primer o segundo nivel). */
const EnlaceRail: React.FC<{ item: MenuItemConfig; plegado: boolean; hijo?: boolean }> = ({ item, plegado, hijo = false }) => (
  <NavLink
    to={item.key}
    end
    className={`fct-rail__item gc-rail__item${hijo ? ' gc-rail__item--hijo' : ''}`}
    title={plegado ? item.label : undefined}
  >
    <span className="gc-rail__icono" aria-hidden="true">{item.icon}</span>
    <span className="gc-rail__etiqueta">{item.label}</span>
  </NavLink>
);

/** Grupo de segundo nivel: botón que despliega sus hijos; abierto si la ruta actual es de uno de ellos. */
const GrupoRail: React.FC<{ item: MenuItemConfig; plegado: boolean }> = ({ item, plegado }) => {
  const { pathname } = useLocation();
  const hijos = item.children ?? [];
  const activo = hijos.some((h) => h.key === pathname);
  const [abierto, setAbierto] = useState<boolean>(activo);
  useEffect(() => { if (activo) setAbierto(true); }, [activo]);
  const idLista = `gc-grupo-${item.key}`;
  return (
    <li className={`gc-rail__grupo${abierto ? ' gc-rail__grupo--abierto' : ''}`}>
      <button
        type="button"
        className={`fct-rail__item gc-rail__item gc-rail__grupo-boton${activo ? ' gc-rail__grupo-boton--activo' : ''}`}
        onClick={() => setAbierto((a) => !a)}
        aria-expanded={abierto}
        aria-controls={idLista}
        title={plegado ? item.label : undefined}
      >
        <span className="gc-rail__icono" aria-hidden="true">{item.icon}</span>
        <span className="gc-rail__etiqueta">{item.label}</span>
        <DownOutlined className="gc-rail__flecha" aria-hidden="true" />
      </button>
      <ul id={idLista} className="gc-rail__sublista" hidden={!abierto}>
        {hijos.map((h) => (
          <li key={h.key}><EnlaceRail item={h} plegado={plegado} hijo /></li>
        ))}
      </ul>
    </li>
  );
};

const GcCarcasa: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [plegado, setPlegado] = useState<boolean>(leerPlegado);
  const items = useMenuItems();

  const alternar = useCallback(() => {
    setPlegado((p) => {
      guardarPlegado(!p);
      return !p;
    });
  }, []);

  return (
    <div className={`fct-carcasa gc-carcasa${plegado ? ' gc-carcasa--plegada' : ''}`}>
      <nav className="fct-rail gc-rail" aria-label="Navegación de gescom.aux">
        <div className="fct-rail__marca gc-rail__marca">
          {plegado ? (
            <FctIsotipo forma="2x4-06" alto={22} tinta="inverso" titulo="gescom.aux" />
          ) : (
            <FctLogotipo forma="2x4-06" nombre="gescom.aux" cuerpo={22} sobreOscuro entorno />
          )}
        </div>
        <ul className="gc-rail__lista">
          {items.map((it) => it.children
            ? <GrupoRail key={it.key} item={it} plegado={plegado} />
            : <li key={it.key}><EnlaceRail item={it} plegado={plegado} /></li>)}
        </ul>
        <div className="gc-rail__pie">
          <UserProfile variante="rail" compacto={plegado} />
          <button
            type="button"
            className="fct-rail__item gc-rail__item gc-rail__plegar"
            onClick={alternar}
            aria-expanded={!plegado}
            aria-label={plegado ? 'Desplegar el menú' : 'Plegar el menú'}
            title={plegado ? 'Desplegar el menú' : undefined}
          >
            <span className="gc-rail__icono" aria-hidden="true">
              {plegado ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            </span>
            <span className="gc-rail__etiqueta">Plegar menú</span>
          </button>
        </div>
      </nav>
      <main className="fct-principal gc-principal">{children}</main>
    </div>
  );
};

export default GcCarcasa;
